import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'

// Theme type definition
export type Theme = 'light' | 'dark' | 'system'

// Theme context return type
export interface ThemeContextReturn {
    currentTheme: Ref<Theme>;
    isDark: ComputedRef<boolean>;
    setTheme: (theme: Theme) => void;
    toggleDark: () => void;
    restoreTheme: () => void;
    isSystemPreference: ComputedRef<boolean>;
}

/**
 * Custom composable for theme management with localStorage persistence
 */
export function useTheme(): ThemeContextReturn {
    const currentTheme = ref<Theme>('system')
    const systemThemeIsDark = ref(false)

    // Check if we're in a browser environment
    const isClient = typeof window !== 'undefined'

    // Computed property to determine if dark mode is active
    const isDark = computed(() => {
        if (currentTheme.value === 'system') {
            return systemThemeIsDark.value
        }
        return currentTheme.value === 'dark'
    })

    /**
     * Update the document classes based on the current theme
     */
    const updateThemeClasses = () => {
        if (!isClient) return

        if (isDark.value) {
            document.documentElement.classList.add('dark')
            document.documentElement.classList.remove('light')
        } else {
            document.documentElement.classList.add('light')
            document.documentElement.classList.remove('dark')
        }
    }

    /**
     * Set the theme and save to localStorage
     */
    const setTheme = (theme: Theme) => {
        currentTheme.value = theme
        if (isClient) {
            localStorage.setItem('theme-preference', theme)
        }
    }

    /**
     * Toggle between light and dark themes
     */
    const toggleDark = () => {
        const newTheme = isDark.value ? 'light' : 'dark'
        setTheme(newTheme)
    }

    /**
     * Check for system theme preference
     */
    const checkSystemTheme = () => {
        if (!isClient) return

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        systemThemeIsDark.value = mediaQuery.matches

        // Add listener for system theme changes
        const handleChange = (e: MediaQueryListEvent) => {
            systemThemeIsDark.value = e.matches
        }

        mediaQuery.addEventListener('change', handleChange)

        // Cleanup listener on component unmount
        onUnmounted(() => {
            mediaQuery.removeEventListener('change', handleChange)
        })
    }

    /**
     * Initialize theme from localStorage or system preference
     */
    const initTheme = () => {
        if (!isClient) return

        // Try to get theme from localStorage
        const savedTheme = localStorage.getItem('theme-preference') as Theme | null

        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
            currentTheme.value = savedTheme
        } else {
            // Default to LIGHT theme instead of system preference
            currentTheme.value = 'light'
            // Save the default preference
            localStorage.setItem('theme-preference', 'light')
        }

        // Immediately update classes based on the loaded theme
        updateThemeClasses()
    }

    // Watch for theme changes and update document classes
    watch(() => isDark.value, () => {
        updateThemeClasses()
    }, { immediate: true })

    // Watch for changes to the current theme
    watch(() => currentTheme.value, () => {
        updateThemeClasses()
    })

    onMounted(() => {
        initTheme()
        checkSystemTheme()
        updateThemeClasses()
    })

    /**
     * Force theme restoration - call this after authentication
     * to ensure theme preference is applied
     */
    const restoreTheme = () => {
        initTheme()
        updateThemeClasses()
    }

    return {
        currentTheme,
        isDark,
        setTheme,
        toggleDark,
        restoreTheme,
        isSystemPreference: computed(() => currentTheme.value === 'system')
    }
}